import React, { useState, useEffect } from "react";
import ApiClient from "../services/api-client"
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import moment from "moment";
import BlockUi from '@availity/block-ui';
import useStore from "../hooks/useStore";
import { isMobile } from "react-device-detect";
import SignatureRenderer from "../components/aggrids/aggrid-cell-renderers/signature-renderer";
import TransactionRenderer from "../components/aggrids/aggrid-cell-renderers/transaction-renderer";
import TransactionActionsRenderer from "../components/aggrids/aggrid-cell-renderers/transaction-action-renderer";
import AllFieldsRequestPaymentRenderer from "../components/aggrids/aggrid-cell-renderers/request-payment-all-fields-renderer";
import AG_GRID_LOCALE_GR from '../locales/gr/aggrid-translation.js';
import AG_GRID_LOCALE_EN from '../locales/gb/aggrid-translation.js';


export default function RequestPaymentSearch() {

    const [gridApi, setGridApi] = useState(null);
    const [columnGridApi, setColumnGridApi] = useState(null);
    const { t, i18n } = useTranslation();
    const [sheight, setSheight] = useState(window.innerHeight - 180);
    const [blockuiLoading, setBlockuiLoading] = useState(false);
    const [fastFilter, setFastFilter] = useState("");
    const [aggridLocal, setAggridLocal] = useState(AG_GRID_LOCALE_GR);
    const [currentPage, setCurrentPage] = useState(0);

    const pagination = true;
    const paginationPageSize = 20;

    const blockUi = (block) => setBlockuiLoading(block);

    var getPageObject = function () {
        var pageNo = gridApi.paginationGetCurrentPage();
        var sortBy = null;
        var order = "asc";
        var filterModel = gridApi.getFilterModel();
        columnGridApi.getColumnState().map((v) => {
            if (v.sort != null) {
                sortBy = v.colId;
                order = v.sort;
            }
        });

        var simpleColumnFilters = [];
        Object.keys(filterModel).forEach(k => {
            simpleColumnFilters.push({
                column: k,
                filter: filterModel[k].filter,
                dateFrom: filterModel[k].dateFrom,
                dateTo: filterModel[k].dateTo,
                filterType: filterModel[k].filterType,
                searchOperator: filterModel[k].type
            });
        });

        return {
            quickSearch: fastFilter,
            pageNo,
            sortBy,
            order,
            pageSize: paginationPageSize,
            simpleColumnFilters
        };
    }

    const getRowData = (params) => {
        var companyId = useStore.getState().selectedCompany.id;
        setTimeout(() => gridApi.showLoadingOverlay());
        var object = getPageObject();
        ApiClient.getCompanyRequestPaymentsPage(companyId, object).then(
            response => {
                const rows = response.data.objectList;
                params.successCallback(rows, response.data.totalElements);
                if (rows.length == 0) {
                    gridApi.showNoRowsOverlay();
                }
            },
            error => {
                gridApi.hideOverlay();
                console.error(
                    (error.response && error.response.data && error.response.data.message) ||
                    error.message || error.toString()
                );
            }
        );
    }

    const onGridReady = params => {
        setGridApi(params.api);
        setColumnGridApi(params.columnApi);
        window.addEventListener('resize', function () {
            setTimeout(() => setSheight(window.innerHeight - 190), 300);
        });
    };

    const onQuickFilterChanged = e => {
        gridApi.fastFilter = e.target.value;
        setFastFilter(e.target.value.trim());
    };

    const onPaginationChanged = (val) => {
        setCurrentPage(val.api.paginationGetCurrentPage());
        val.api.hideOverlay();
    };

    useEffect(() => {
        if (gridApi) {
            const dataSource = {
                getRows: (params) => {
                    let mounted = true;
                    if (mounted) getRowData(params);
                    return () => mounted = false;
                }
            };
            const timer = setTimeout(() => gridApi.setDatasource(dataSource), 1000);
            return () => clearTimeout(timer);
        }
    }, [fastFilter]);

    useEffect(() => {
        if (gridApi) {
            const dataSource = {
                getRows: (params) => {
                    let mounted = true;
                    if (mounted) getRowData(params);
                    return () => mounted = false;
                }
            };
            if (i18n.language == "el-GR" || i18n.language == "gr") {
                setAggridLocal(AG_GRID_LOCALE_GR);
            } else {
                setAggridLocal(AG_GRID_LOCALE_EN);
            }
            gridApi.setDatasource(dataSource);
        }
    }, [gridApi]);

    const cellRendererParams = { blockUi };

    const desktopColumnDefs = [
        {
            headerName: t("RequestPaymentHeader"),
            children: [
                { field: "signature", flex: 5, minWidth: 200, headerName: t("signature"), sortable: true, filter: "agTextColumnFilter", cellRenderer: SignatureRenderer },
                { field: "timestamp", flex: 1, minWidth: 200, headerName: t("TimeStamp"), cellClass: "with-border", sortable: true, filter: "agDateColumnFilter", valueFormatter: (params) => moment(params.value).format('DD/MM/YYYY HH:mm') },
            ]
        },
        {
            headerName: t("TransactionHeader"),
            children: [
                { field: "posTransaction.status", flex: 2, minWidth: 120, headerName: t("Status"), sortable: true, filter: "agTextColumnFilter", valueFormatter: (params) => t(params.value) },
                { field: "posTransaction.result", flex: 2, minWidth: 120, headerName: t("Result"), sortable: true, filter: "agTextColumnFilter", valueFormatter: (params) => t(params.value) },
                { field: "posTransaction.transactionId", flex: 3, minWidth: 200, headerName: t("TransactionId"), sortable: true, filter: "agTextColumnFilter", cellRenderer: TransactionRenderer },
                { field: "posTransaction.refundTransactionId", flex: 3, minWidth: 200, headerName: t("RefundTransactionId"), cellStyle: { color: 'red' }, sortable: true, filter: "agTextColumnFilter" },
                { field: "posTransaction.timestamp", flex: 2, minWidth: 200, headerName: t("TimeStamp"), sortable: true, filter: "agDateColumnFilter", valueFormatter: (params) => params.value ? moment(params.value).format('DD/MM/YYYY HH:mm') : null },
                { field: "posTransaction.amount", flex: 1, minWidth: 120, headerName: t("Amount"), cellClass: "with-border", sortable: true, filter: "agNumberColumnFilter", valueFormatter: (params) => params.value ? (params.value / 100).toFixed(2) : null },
            ]
        },
        {
            headerName: t("InvoiceHeader"),
            children: [
                { field: "saleInstance.seriesNumber", flex: 1, minWidth: 200, headerName: t("SeriesNumber"), sortable: true, filter: "agTextColumnFilter" },
                { field: "saleInstance.invoiceUid", flex: 4, minWidth: 200, headerName: t("InvoiceUid"), sortable: true, filter: "agTextColumnFilter" },
                { field: "saleInstance.mark", flex: 1, minWidth: 200, headerName: t("Mark"), sortable: true, filter: "agTextColumnFilter" },
                { field: "action", flex: 1, minWidth: 120, headerName: t("Actions"), cellRenderer: TransactionActionsRenderer, cellRendererParams },
            ]
        }
    ];

    const mobileColumnDefs = [
        { field: "signature", flex: 5, headerName: t(""), sortable: true, filter: "agTextColumnFilter", cellRenderer: AllFieldsRequestPaymentRenderer },
        { field: "timestamp", hide: true, headerName: t("TimeStamp"), sortable: true, filter: "agDateColumnFilter", valueFormatter: (params) => moment(params.value).format('DD/MM/YYYY HH:mm') },
        { field: "posTransaction.status", hide: true, headerName: t("Status"), sortable: true, filter: "agTextColumnFilter", valueFormatter: (params) => t(params.value) },
        { field: "posTransaction.result", hide: true, headerName: t("Result"), sortable: true, filter: "agTextColumnFilter", valueFormatter: (params) => t(params.value) },
        { field: "posTransaction.transactionId", hide: true, headerName: t("TransactionId"), sortable: true, filter: "agTextColumnFilter" },
        { field: "posTransaction.timestamp", hide: true, headerName: t("TimeStamp"), sortable: true, filter: "agDateColumnFilter", valueFormatter: (params) => params.value ? moment(params.value).format('DD/MM/YYYY HH:mm') : null },
        { field: "posTransaction.amount", hide: true, headerName: t("Amount"), sortable: true, filter: "agNumberColumnFilter", valueFormatter: (params) => params.value ? (params.value / 100).toFixed(2) : null },
        { field: "saleInstance.seriesNumber", hide: true, headerName: t("SeriesNumber"), sortable: true, filter: "agTextColumnFilter" },
        { field: "saleInstance.invoiceUid", hide: true, headerName: t("InvoiceUid"), sortable: true, filter: "agTextColumnFilter" },
        { field: "saleInstance.mark", hide: true, headerName: t("Mark"), sortable: true, filter: "agTextColumnFilter" },
        { field: "action", flex: 1, headerName: t("Actions"), cellRenderer: TransactionActionsRenderer, cellRendererParams },
    ];

    const commonGridProps = {
        pagination,
        paginationPageSize,
        onGridReady,
        style: { width: '100%', height: '100%' },
        animateRows: true,
        suppressCellSelection: true,
        suppressRowTransform: true,
        overlayNoRowsTemplate: '<span style="padding: 10px; color:#013289">' + t('noEntries') + '</span>',
        overlayLoadingTemplate: '<span class="ag-overlay-loading-center"><img src="loading.gif">' + t('aggridPageLoadMess') + '</span>',
        onPaginationChanged,
        rowModelType: 'infinite',
        cacheBlockSize: paginationPageSize,
        gridOptions: { suppressMenuHide: true },
        tooltipHideDelay: 0,
        enableBrowserTooltips: false,
        localeText: aggridLocal,
        defaultColDef: {
            sortable: true,
            wrapText: false,
            suppressHorizontalScroll: true,
            suppressMovable: true,
        }
    };

    return (
        <BlockUi tag="div" blocking={blockuiLoading}>
            <div className="container-fluid" style={{ padding: 0 }}>
                <header className="jumbotron search_header">
                    <div className="d-flex bd-highlight">
                        <div className="me-auto p-0 bd-highlight">
                            <h5 className="subtitle skin">{t("posTransactionAndRequestPaymentSearch")}</h5>
                        </div>
                    </div>
                </header>
                <div style={{ width: '100%', height: '100%' }}>
                    <div className="row">
                        <div className="d-flex justify-content-between">
                            <div className="offset-md-4 col-md-4 text-center">
                                <input className="form-control" id="searchRequestPaymentsInput" placeholder={t("Search")} onChange={onQuickFilterChanged} />
                            </div>
                        </div>
                    </div>
                    {
                        !isMobile ?
                            <div id="myGrid" style={{ height: '100%', width: '100%' }} className="ag-theme-alpine-dark product-search-grid">
                                <div className="ag-theme-alpine" style={{ height: sheight, width: '100%' }}>
                                    <AgGridReact {...commonGridProps} columnDefs={desktopColumnDefs} />
                                </div>
                            </div>
                            :
                            <div id="myGrid" style={{ height: '100%', width: '100%' }} className="ag-theme-alpine-dark product-mobile-table-container">
                                <div className="ag-theme-alpine" style={{ height: sheight, width: '100%' }}>
                                    <AgGridReact {...commonGridProps} rowHeight={136} columnDefs={mobileColumnDefs} />
                                </div>
                            </div>
                    }
                </div>
            </div>
        </BlockUi>
    );
}
